import { IconButton } from '@creditornot/cb-button';
import { Checkmark, Cross, InfoCircleFilled } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { Popover } from '@creditornot/cb-popover';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { generateAiProductFamilySuggestion } from 'apis/offer-listing-service/api';
import { GenerateAiProductFamilySuccessResponse } from 'apis/offer-listing-service/types';
import LoadingPendingFlowerSpinner from 'common/components/icons/LoadingPendingFlowerSpinner';
import { MultiLevelSelect } from 'modules/menu/components/MultiLevelSelect';
import { useMenuModuleContext } from 'modules/menu/context';
import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';
import { trackProductFamilySuggestion } from 'modules/shared-menu/telemetry';

import { BaseField } from './types';
import { FormRowRenderer } from '../FormRowRenderer';
import { DisplayText, StyledInputContainer } from '../OfferDetailsFormFields.styles';
import { findTranslation } from '../transformers';
import { BasicDetails, FormValues } from '../types';
import { ProductHierarchyOption } from '../utils';

export type ProductFamilySuggestionTrackingState = {
  status: 'none' | 'generated' | 'touched';
  suggestedProductFamilyId?: string;
};

type ProductFamilyProps = BaseField & {
  productHierarchyOptions: ProductHierarchyOption[];
  handleRestrictedPropertyChange: ({
    productHierarchyLevelId,
    alcoholPercentage,
  }: {
    productHierarchyLevelId?: string | null | undefined;
    alcoholPercentage?: string | number | undefined;
  }) => void;
  isVenueLevel: boolean;
  updateProductFamilyTrackingState: (state: ProductFamilySuggestionTrackingState) => void;
};

const InfoMessage = styled.div`
  display: inline-flex;
  align-items: flex-start;
  margin-block-start: 8px;
  gap: 6px;
  max-width: 280px;
  ${typographyCss.Small()}
`;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  flex-shrink: 0;
  margin-block-start: 2px;
`;

const SuggestionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${cbSemanticCssVars.borderPrimary};
  border-radius: 8px;
  padding: 8px;
  color: ${cbSemanticCssVars.textPrimary};
`;

const SuggestedProductFamilyName = styled.div<{ $loading?: boolean }>`
  /* TODO: color-bg-surface-brand */
  background: ${colors.wolt8};
  /* TODO: color-text-brand */
  color: ${colors.wolt100};
  /* TODO: color-text-brand */
  border: 1px dashed ${colors.wolt100};
  border-radius: 8px;
  padding: 2px 4px;
  ${typographyCss.Small()}
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
`;

const ButtonContainer = styled.div`
  display: flex;
`;

const StyledIconButton = styled(IconButton).attrs({
  variant: 'neutral',
  size: 'tiny',
  background: 'transparent',
  alignment: 'iconEdges',
})`
  width: 14px;
  height: 14px;
  margin-inline-start: 16px;
`;

const LoadingIcon = styled(LoadingPendingFlowerSpinner).attrs({
  width: 14,
  height: 14,
})`
  flex-shrink: 0;
`;

const TooltipMessage = styled.div`
  ${typographyCss.Body3()};
  padding: 8px;
`;

const getHierarchyLabelPath = (
  hierarchyLevels: ProductHierarchyOption[],
  id: string = '',
  path: string[] = [],
): string[] => {
  const currentLevel = hierarchyLevels.find(level => level.value === id);
  if (!currentLevel) {
    return path;
  }
  const currentPath = [currentLevel.label, ...path];
  return !currentLevel.parentId
    ? currentPath
    : getHierarchyLabelPath(hierarchyLevels, currentLevel.parentId, currentPath);
};

const truncateStringFromStart = (str: string) => {
  const maxLength = 35;
  if (str.length <= maxLength) {
    return str;
  }
  return '...' + str.slice(-maxLength);
};

const ProductFamilyLoading = () => (
  <SuggestionContainer>
    <SuggestedProductFamilyName $loading>
      <FormattedMessage id="menu-item.product-family-ai-suggestion.loading" />
    </SuggestedProductFamilyName>
    <LoadingIcon />
  </SuggestionContainer>
);

const SuggestedProductFamily = ({
  onCancel,
  onAccept,
  name,
  cancelMessage,
  acceptMessage,
}: {
  onCancel: () => void;
  onAccept: () => void;
  name: string;
  cancelMessage: string;
  acceptMessage: string;
}) => (
  <SuggestionContainer>
    <SuggestedProductFamilyName>{name}</SuggestedProductFamilyName>
    <ButtonContainer>
      <Popover placement="top" content={<TooltipMessage>{cancelMessage}</TooltipMessage>} openOnHover>
        {({ refProps }) => (
          <StyledIconButton {...refProps} onClick={onCancel}>
            <Cross />
          </StyledIconButton>
        )}
      </Popover>
      <Popover placement="top" content={<TooltipMessage>{acceptMessage}</TooltipMessage>} openOnHover>
        {({ refProps }) => (
          <StyledIconButton {...refProps} onClick={onAccept}>
            <Checkmark />
          </StyledIconButton>
        )}
      </Popover>
    </ButtonContainer>
  </SuggestionContainer>
);

export const ProductFamily = ({
  sidePanelMode,
  productHierarchyOptions,
  handleRestrictedPropertyChange,
  offer,
  isVenueLevel,
  updateProductFamilyTrackingState,
}: ProductFamilyProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const { categories, selection } = useSelectionData();
  const { values, active } = useFormState<FormValues>();
  const form = useForm<FormValues>();
  const intl = useIntl();
  const { country } = useSelectionInfoContext();
  const [suggestedProductFamily, setSuggestedProductFamily] = useState<ProductHierarchyOption | null>(null);
  const { venue } = useMenuModuleContext();
  const previousActive = useRef(active);
  const previousCategory = useRef(values.categoryId);
  const [productFamilySuggestionRejected, setProductFamilySuggestionRejected] = useState<string | null>(null);

  const productFamilyAiSuggestionPayload = useMemo(() => {
    const category = categories.find(category => category.id === values.categoryId);
    return {
      name: values.name,
      alcohol_percentage: values.alcoholPercentage.toString(),
      country_code: country,
      description: values.description || undefined,
      category:
        category?.is_wolt_special || !category
          ? undefined
          : findTranslation(category?.name, selection.primary_language),
      product_line: venue && venue.product_line,
    };
  }, [
    categories,
    values.name,
    values.alcoholPercentage,
    values.description,
    values.categoryId,
    selection.primary_language,
    country,
    venue,
  ]);

  const { mutate: generateProductFamily, isLoading: isSearchingProductFamily } = useMutation(
    () => generateAiProductFamilySuggestion(productFamilyAiSuggestionPayload),
    {
      onSuccess: (data: GenerateAiProductFamilySuccessResponse) => {
        updateProductFamilyTrackingState({
          status: 'generated',
          suggestedProductFamilyId: data.product_family_id,
        });
        setSuggestedProductFamily(
          productHierarchyOptions.find(p => p.secondaryContent === data.product_family_id) ?? null,
        );
      },
    },
  );

  useEffect(() => {
    if (
      !isVenueLevel || // Only support venue level for now
      sidePanelMode === 'preview' ||
      values.productHierarchyLevelId ||
      !values.name ||
      (previousActive.current === active && previousCategory.current === values.categoryId)
    ) {
      return;
    }

    if (
      previousActive.current === 'name' ||
      previousActive.current === 'description' ||
      previousCategory.current !== values.categoryId
    ) {
      generateProductFamily();
    }

    previousActive.current = active;
    previousCategory.current = values.categoryId;
  }, [
    active,
    generateProductFamily,
    isVenueLevel,
    productFamilyAiSuggestionPayload,
    sidePanelMode,
    values.categoryId,
    values.name,
    values.productHierarchyLevelId,
  ]);

  const isOverTheCounterProductHierarchyLevel = useCallback(
    (value: string): boolean =>
      !!productHierarchyOptions.find(option => option.value === value)?.tags.includes('over_the_counter'),
    [productHierarchyOptions],
  );

  const productHierarchyDisplayName = useMemo(
    () => getHierarchyLabelPath(productHierarchyOptions, values.productHierarchyLevelId).join(' / '),
    [productHierarchyOptions, values.productHierarchyLevelId],
  );

  const updateRestriction = useCallback(
    (value?: string) => {
      if (!value) {
        return;
      }

      handleRestrictedPropertyChange({ productHierarchyLevelId: value });
      if (
        (!value || !isOverTheCounterProductHierarchyLevel(value)) &&
        form.getFieldState('isRestrictedItem')?.value &&
        form.getFieldState('isOTCRestricted')?.value === true
      ) {
        form.change('isRestrictedItem', false);
        form.change('isOTCRestricted', false);
      }
    },
    [form, handleRestrictedPropertyChange, isOverTheCounterProductHierarchyLevel],
  );

  const onCancelSuggestion = () => {
    if (suggestedProductFamily?.value) {
      setProductFamilySuggestionRejected(suggestedProductFamily.value);
    }
    setSuggestedProductFamily(null);
  };

  const onAcceptSuggestion = () => {
    form.change('productHierarchyLevelId', suggestedProductFamily?.value);
    updateRestriction(suggestedProductFamily?.value);

    if (suggestedProductFamily?.value) {
      updateProductFamilyTrackingState({
        status: 'touched',
        suggestedProductFamilyId: suggestedProductFamily?.value,
      });
      trackProductFamilySuggestion.suggestionAccepted({
        offerId: offer?.id,
        venueId: venue.id,
        selectionId: selection.id,
        productFamilyId: suggestedProductFamily.value,
        listingAlcoholContent: null,
        listingCategoryName: productFamilyAiSuggestionPayload.category,
        listingCountryCode: productFamilyAiSuggestionPayload.country_code,
        listingOfferDescription: productFamilyAiSuggestionPayload.description,
        listingOfferName: productFamilyAiSuggestionPayload.name,
        listingProductLine: productFamilyAiSuggestionPayload.product_line,
      });
    }
    setSuggestedProductFamily(null);
  };

  const sendRejectedProductFamilySuggestion = (value: string) => {
    if (productFamilySuggestionRejected) {
      updateProductFamilyTrackingState({
        status: 'touched',
        suggestedProductFamilyId: suggestedProductFamily?.value,
      });
      trackProductFamilySuggestion.suggestionRejected({
        offerId: offer?.id,
        venueId: venue.id,
        selectionId: selection.id,
        suggestedProductFamilyId: productFamilySuggestionRejected,
        selectedProductFamilyId: value,
        listingAlcoholContent: null,
        listingCategoryName: productFamilyAiSuggestionPayload.category,
        listingCountryCode: productFamilyAiSuggestionPayload.country_code,
        listingOfferDescription: productFamilyAiSuggestionPayload.description,
        listingOfferName: productFamilyAiSuggestionPayload.name,
        listingProductLine: productFamilyAiSuggestionPayload.product_line,
      });
    }
  };

  const previewComponent = <DisplayText dir="auto">{productHierarchyDisplayName || '-'}</DisplayText>;

  const renderEditComponents = () => {
    if (isSearchingProductFamily) {
      return <ProductFamilyLoading />;
    }

    if (suggestedProductFamily) {
      return (
        <SuggestedProductFamily
          onCancel={onCancelSuggestion}
          onAccept={onAcceptSuggestion}
          name={getHierarchyLabelPath(productHierarchyOptions, suggestedProductFamily.value).join(' / ')}
          cancelMessage={intl.formatMessage({ id: 'menu-item.product-family-ai-suggestion.reject-button' })}
          acceptMessage={intl.formatMessage({ id: 'menu-item.product-family-ai-suggestion.accept-button' })}
        />
      );
    }

    return (
      <Field<BasicDetails['productHierarchyLevelId']> name="productHierarchyLevelId">
        {field => (
          <StyledInputContainer>
            <MultiLevelSelect
              {...field.input}
              value={field.input.value ?? null}
              overriddenDisplayValue={truncateStringFromStart(productHierarchyDisplayName)}
              onChange={value => {
                field.input.onChange(value);
                updateRestriction(value);
                sendRejectedProductFamilySuggestion(value);
              }}
              onClearClick={() => field.input.onChange(undefined)}
              options={productHierarchyOptions}
              placeholder={intl.formatMessage({ id: 'menu.selectOrSearch' })}
              disabled={!!values.productId}
            />
          </StyledInputContainer>
        )}
      </Field>
    );
  };

  const editComponent = (
    <>
      {renderEditComponents()}
      {isVenueLevel && (
        <InfoMessage>
          <InfoIcon />
          <FormattedMessage id="menu-item.product-family-ai-suggestion.info" />
        </InfoMessage>
      )}
    </>
  );

  return (
    <FormRowRenderer
      hasContentEditPermission={menuPermissions.contentEdit}
      label={intl.formatMessage({ id: 'menu-item.product-family' })}
      previewComponent={previewComponent}
      editComponent={editComponent}
      sidePanelMode={sidePanelMode}
      offer={offer}
    />
  );
};
